import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Підготовка до НМТ» for the 10th and 11th grades: Ukrainian and mathematics, and two subjects
 * to choose; practice tests from questions that are part of the app (no answers go to a server),
 * an approximate score and a personal plan from the topics that went wrong. Practice only: the
 * official materials are УЦОЯО's.
 */
export type NmtSubject = 'ukr' | 'math' | 'history' | 'eng' | 'bio' | 'phys' | 'chem' | 'geo';

export const NMT_SUBJECTS: ReadonlyArray<{
  key: NmtSubject;
  name: string;
  emoji: string;
  required: boolean;
}> = [
  { key: 'ukr', name: 'Українська мова', emoji: '📝', required: true },
  { key: 'math', name: 'Математика', emoji: '📐', required: true },
  { key: 'history', name: 'Історія України', emoji: '🏛️', required: false },
  { key: 'eng', name: 'Англійська мова', emoji: '🇬🇧', required: false },
  { key: 'bio', name: 'Біологія', emoji: '🧬', required: false },
  { key: 'phys', name: 'Фізика', emoji: '⚛️', required: false },
  { key: 'chem', name: 'Хімія', emoji: '🧪', required: false },
  { key: 'geo', name: 'Географія', emoji: '🌍', required: false },
];

/** The main session is in May–June; the day here is an estimate for the countdown. */
export const NMT_DAY = '2027-05-20';
/** УЦОЯО, the official practice and registration */
export const NMT_OFFICIAL = 'https://testportal.gov.ua';

export interface NmtQuestion {
  subject: NmtSubject;
  topic: string;
  text: string;
  options: readonly string[];
  correct: number;
  explain: string;
}

export const NMT_BANK: readonly NmtQuestion[] = [
  // --- Українська мова ----------------------------------------------------------------------------
  {
    subject: 'ukr',
    topic: 'Правопис «пів»',
    text: 'У якому рядку всі слова написано правильно?',
    options: [
      'пів-яблука, пів-Києва, півкола',
      'пів яблука, пів-Києва, півкола',
      'пів яблука, пів Києва, пів кола',
      'півяблука, півКиєва, півкола',
    ],
    correct: 1,
    explain:
      '«Пів» з загальною назвою — окремо (пів яблука), з власною — через дефіс (пів-Києва); «півкола» — цілісне слово.',
  },
  {
    subject: 'ukr',
    topic: 'Наголос',
    text: 'У якому слові наголос падає на ПЕРШИЙ склад?',
    options: ['фартух', 'кропива', 'черговий', 'читання'],
    correct: 0,
    explain: 'Правильно: фАртух, кропивА, черговИй, читАння.',
  },
  {
    subject: 'ukr',
    topic: 'Пунктуація',
    text: 'У якому реченні пропущено кому?',
    options: [
      'Сонце сховалося за хмари і почав накрапати дощ.',
      'Діти бігали і сміялися.',
      'Ми читали книжки й журнали.',
      'Вітер гнув дерева до самої землі.',
    ],
    correct: 0,
    explain:
      'Це складносурядне речення з двома основами: «Сонце сховалося…, і почав накрапати дощ».',
  },
  {
    subject: 'ukr',
    topic: 'Лексика',
    text: 'Яке слово є синонімом до слова «відважний»?',
    options: ['сміливий', 'обережний', 'лагідний', 'щедрий'],
    correct: 0,
    explain: 'Відважний — той, хто не боїться: сміливий, хоробрий.',
  },
  {
    subject: 'ukr',
    topic: 'Частини мови',
    text: 'У якому рядку всі слова — прислівники?',
    options: [
      'швидко, вчора, додому',
      'швидкий, вчора, дім',
      'бігти, тут, зранку',
      'весело, веселий, веселість',
    ],
    correct: 0,
    explain: 'Прислівники відповідають на питання як? коли? куди? і не змінюються.',
  },
  {
    subject: 'ukr',
    topic: 'Апостроф',
    text: 'У якому слові на місці пропуску треба писати апостроф?',
    options: ['пір_я', 'цв_ях', 'св_ято', 'різдв_яний'],
    correct: 0,
    explain:
      'Апостроф пишемо після «р» в кінці складу: пір’я. Після губного з іншим приголосним перед ним — ні: цвях, свято, різдвяний.',
  },
  {
    subject: 'ukr',
    topic: 'Милозвучність',
    text: 'Оберіть правильний варіант: «Він ___».',
    options: ['у школі', 'в школі', 'вшколі', 'ув школі'],
    correct: 0,
    explain: 'Після приголосного перед приголосним пишемо «у»: він у школі.',
  },
  {
    subject: 'ukr',
    topic: 'Пунктуація',
    text: 'У якому реченні між підметом і присудком треба поставити тире?',
    options: ['Київ столиця України.', 'Я учень.', 'Небо ніби море.', 'Сьогодні тепло.'],
    correct: 0,
    explain: 'Підмет і присудок — іменники без зв’язки: «Київ — столиця України».',
  },
  // --- Математика ----------------------------------------------------------------------------------
  {
    subject: 'math',
    topic: 'Відсотки',
    text: 'Ціну 800 грн знизили на 15%. Яка нова ціна?',
    options: ['680 грн', '785 грн', '120 грн', '700 грн'],
    correct: 0,
    explain: '800 · 0,85 = 680 грн.',
  },
  {
    subject: 'math',
    topic: 'Лінійні рівняння',
    text: 'Розв’яжіть рівняння 2x − 5 = 11.',
    options: ['3', '8', '−3', '16'],
    correct: 1,
    explain: '2x = 16, x = 8.',
  },
  {
    subject: 'math',
    topic: 'Квадратні рівняння',
    text: 'Знайдіть суму коренів рівняння x² − 7x + 10 = 0.',
    options: ['7', '10', '−7', '3'],
    correct: 0,
    explain: 'Корені 2 і 5; за теоремою Вієта сума дорівнює 7.',
  },
  {
    subject: 'math',
    topic: 'Степені',
    text: 'Обчисліть 2⁻³.',
    options: ['−8', '1/8', '−6', '8'],
    correct: 1,
    explain: '2⁻³ = 1/2³ = 1/8.',
  },
  {
    subject: 'math',
    topic: 'Функції',
    text: 'Яка з функцій зростає на всій області визначення?',
    options: ['y = −2x + 1', 'y = 3x − 4', 'y = x²', 'y = 5'],
    correct: 1,
    explain: 'Лінійна функція зростає, коли кутовий коефіцієнт додатний: 3 > 0.',
  },
  {
    subject: 'math',
    topic: 'Планіметрія',
    text: 'Катети прямокутного трикутника — 6 і 8. Знайдіть гіпотенузу.',
    options: ['10', '14', '12', '√28'],
    correct: 0,
    explain: 'За теоремою Піфагора: √(36 + 64) = 10.',
  },
  {
    subject: 'math',
    topic: 'Планіметрія',
    text: 'Площа круга радіуса 3 дорівнює:',
    options: ['6π', '9π', '3π', '36π'],
    correct: 1,
    explain: 'S = πr² = 9π.',
  },
  {
    subject: 'math',
    topic: 'Ймовірність',
    text: 'Кидають гральний кубик. Яка ймовірність, що випаде парне число?',
    options: ['1/6', '1/3', '1/2', '2/3'],
    correct: 2,
    explain: 'Парних граней три з шести: 3/6 = 1/2.',
  },
  {
    subject: 'math',
    topic: 'Логарифми',
    text: 'Обчисліть log₂ 32.',
    options: ['4', '5', '16', '6'],
    correct: 1,
    explain: '2⁵ = 32, отже log₂ 32 = 5.',
  },
  {
    subject: 'math',
    topic: 'Тригонометрія',
    text: 'Обчисліть sin 30°.',
    options: ['1/2', '√3/2', '√2/2', '1'],
    correct: 0,
    explain: 'sin 30° = 1/2.',
  },
  // --- Історія України -------------------------------------------------------------------------------
  {
    subject: 'history',
    topic: 'Київська Русь',
    text: 'У якому році князь Володимир запровадив християнство на Русі?',
    options: ['988', '1054', '882', '1240'],
    correct: 0,
    explain: 'Хрещення Русі традиційно датують 988 роком.',
  },
  {
    subject: 'history',
    topic: 'Козацька доба',
    text: 'Хто очолював Національно-визвольну війну середини XVII ст.?',
    options: ['Іван Мазепа', 'Богдан Хмельницький', 'Петро Сагайдачний', 'Пилип Орлик'],
    correct: 1,
    explain: 'Війну 1648–1657 рр. очолював гетьман Богдан Хмельницький.',
  },
  {
    subject: 'history',
    topic: 'Козацька доба',
    text: 'Автор Конституції 1710 року:',
    options: ['Пилип Орлик', 'Іван Мазепа', 'Михайло Грушевський', 'Тарас Шевченко'],
    correct: 0,
    explain: '«Пакти й Конституції прав і вольностей Війська Запорозького» уклав Пилип Орлик.',
  },
  {
    subject: 'history',
    topic: 'Українська революція',
    text: 'Головою Центральної Ради був:',
    options: [
      'Михайло Грушевський',
      'Володимир Винниченко',
      'Симон Петлюра',
      'Павло Скоропадський',
    ],
    correct: 0,
    explain: 'Центральну Раду очолював Михайло Грушевський.',
  },
  {
    subject: 'history',
    topic: 'Українська революція',
    text: 'Акт Злуки УНР і ЗУНР проголошено:',
    options: ['22 січня 1919 р.', '24 серпня 1991 р.', '1 листопада 1918 р.', '29 квітня 1918 р.'],
    correct: 0,
    explain: 'Акт Злуки проголосили в Києві 22 січня 1919 року.',
  },
  {
    subject: 'history',
    topic: 'Україна в ХХ ст.',
    text: 'Голодомор — геноцид українського народу — стався в:',
    options: ['1921–1922 рр.', '1932–1933 рр.', '1946–1947 рр.', '1941–1942 рр.'],
    correct: 1,
    explain: 'Голодомор 1932–1933 років визнано геноцидом українського народу.',
  },
  {
    subject: 'history',
    topic: 'Незалежна Україна',
    text: 'Акт проголошення незалежності України ухвалено:',
    options: ['24 серпня 1991 р.', '1 грудня 1991 р.', '22 січня 1919 р.', '16 липня 1990 р.'],
    correct: 0,
    explain: '24 серпня 1991 року Верховна Рада ухвалила Акт; 1 грудня його підтвердив референдум.',
  },
  {
    subject: 'history',
    topic: 'Незалежна Україна',
    text: 'Конституцію України ухвалено:',
    options: ['28 червня 1996 р.', '24 серпня 1991 р.', '1 грудня 1991 р.', '8 грудня 1991 р.'],
    correct: 0,
    explain: 'Конституцію ухвалили 28 червня 1996 року — це День Конституції.',
  },
  // --- Англійська мова -------------------------------------------------------------------------------
  {
    subject: 'eng',
    topic: 'Present Simple',
    text: 'She ___ to school every day.',
    options: ['go', 'goes', 'is go', 'going'],
    correct: 1,
    explain: 'Present Simple, he/she/it: goes.',
  },
  {
    subject: 'eng',
    topic: 'Past Simple',
    text: 'I ___ this film yesterday.',
    options: ['have seen', 'saw', 'see', 'was seen'],
    correct: 1,
    explain: '«Yesterday» — час минув: Past Simple, saw.',
  },
  {
    subject: 'eng',
    topic: 'Conditionals',
    text: 'If it ___ tomorrow, we will stay at home.',
    options: ['rains', 'will rain', 'rained', 'raining'],
    correct: 0,
    explain: 'First Conditional: if + Present Simple, will + verb.',
  },
  {
    subject: 'eng',
    topic: 'Vocabulary',
    text: 'Choose the synonym of «huge».',
    options: ['tiny', 'enormous', 'quick', 'quiet'],
    correct: 1,
    explain: 'Huge = enormous, very big.',
  },
  {
    subject: 'eng',
    topic: 'Some / any',
    text: 'There isn’t ___ milk in the fridge.',
    options: ['some', 'any', 'many', 'a'],
    correct: 1,
    explain: 'У запереченнях — any; milk незлічуване.',
  },
  {
    subject: 'eng',
    topic: 'Vocabulary',
    text: '«To look forward to» means:',
    options: ['to wait with pleasure', 'to look behind', 'to forget', 'to search'],
    correct: 0,
    explain: 'Look forward to — чекати з нетерпінням.',
  },
  // --- Біологія ----------------------------------------------------------------------------------------
  {
    subject: 'bio',
    topic: 'Клітина',
    text: 'Де в клітині еукаріотів міститься переважна частина ДНК?',
    options: ['у ядрі', 'у рибосомах', 'у вакуолі', 'у клітинній стінці'],
    correct: 0,
    explain: 'Основна частина ДНК — у хромосомах ядра.',
  },
  {
    subject: 'bio',
    topic: 'Клітина',
    text: 'Яку функцію виконують мітохондрії?',
    options: ['синтез білка', 'утворення енергії (АТФ)', 'фотосинтез', 'зберігання води'],
    correct: 1,
    explain: 'Мітохондрії — «енергостанції» клітини: тут синтезується АТФ.',
  },
  {
    subject: 'bio',
    topic: 'Генетика',
    text: 'Скільки хромосом у соматичній клітині людини?',
    options: ['23', '44', '46', '48'],
    correct: 2,
    explain: '46 хромосом — 23 пари; у статевих клітинах — 23.',
  },
  {
    subject: 'bio',
    topic: 'Генетика',
    text: 'Схрестили Aa × Aa. Яка частка нащадків матиме рецесивну ознаку?',
    options: ['1/4', '1/2', '3/4', '0'],
    correct: 0,
    explain: 'Розщеплення 1 AA : 2 Aa : 1 aa — рецесивна ознака в 1/4.',
  },
  {
    subject: 'bio',
    topic: 'Людина',
    text: 'Яку групу крові (за системою AB0) вважають «універсальним донором»?',
    options: ['I (0)', 'II (A)', 'III (B)', 'IV (AB)'],
    correct: 0,
    explain: 'Еритроцити групи I (0) не мають антигенів A і B.',
  },
  {
    subject: 'bio',
    topic: 'Екологія',
    text: 'Організми, що самі утворюють органічні речовини з неорганічних, — це:',
    options: ['автотрофи', 'гетеротрофи', 'паразити', 'редуценти'],
    correct: 0,
    explain: 'Автотрофи (рослини, ціанобактерії) — продуценти екосистем.',
  },
  // --- Фізика ---------------------------------------------------------------------------------------------
  {
    subject: 'phys',
    topic: 'Механіка',
    text: 'Тіло рухається рівномірно зі швидкістю 20 м/с. Який шлях воно пройде за 5 с?',
    options: ['4 м', '100 м', '25 м', '15 м'],
    correct: 1,
    explain: 's = vt = 20 · 5 = 100 м.',
  },
  {
    subject: 'phys',
    topic: 'Механіка',
    text: 'Одиниця сили в СІ:',
    options: ['джоуль', 'ньютон', 'ват', 'паскаль'],
    correct: 1,
    explain: 'Силу вимірюють у ньютонах.',
  },
  {
    subject: 'phys',
    topic: 'Електрика',
    text: 'За законом Ома для ділянки кола сила струму I дорівнює:',
    options: ['U · R', 'U / R', 'R / U', 'U + R'],
    correct: 1,
    explain: 'I = U / R.',
  },
  {
    subject: 'phys',
    topic: 'Енергія',
    text: 'Кінетична енергія тіла дорівнює:',
    options: ['mgh', 'mv²/2', 'kx²/2', 'Fs'],
    correct: 1,
    explain: 'Eк = mv²/2; mgh — потенціальна енергія.',
  },
  {
    subject: 'phys',
    topic: 'Оптика',
    text: 'Швидкість світла у вакуумі приблизно дорівнює:',
    options: ['300 000 км/с', '300 км/с', '340 м/с', '3 000 км/с'],
    correct: 0,
    explain: 'c ≈ 3 · 10⁸ м/с = 300 000 км/с; 340 м/с — швидкість звуку в повітрі.',
  },
  {
    subject: 'phys',
    topic: 'Молекулярна фізика',
    text: 'Під час кипіння (за сталого тиску) температура рідини:',
    options: ['зростає', 'не змінюється', 'зменшується', 'спочатку зростає, потім падає'],
    correct: 1,
    explain: 'Уся енергія йде на пароутворення — температура стала.',
  },
  // --- Хімія ----------------------------------------------------------------------------------------------
  {
    subject: 'chem',
    topic: 'Будова атома',
    text: 'Скільки протонів у ядрі атома Оксигену?',
    options: ['6', '8', '16', '2'],
    correct: 1,
    explain: 'Порядковий номер Оксигену — 8.',
  },
  {
    subject: 'chem',
    topic: 'Класи сполук',
    text: 'Яка речовина є кислотою?',
    options: ['NaOH', 'HCl', 'NaCl', 'CaO'],
    correct: 1,
    explain: 'HCl — хлоридна кислота; NaOH — луг, NaCl — сіль, CaO — оксид.',
  },
  {
    subject: 'chem',
    topic: 'Органічна хімія',
    text: 'Загальна формула алканів:',
    options: ['CₙH₂ₙ', 'CₙH₂ₙ₊₂', 'CₙH₂ₙ₋₂', 'CₙHₙ'],
    correct: 1,
    explain: 'Алкани — насичені вуглеводні: CₙH₂ₙ₊₂ (метан CH₄).',
  },
  {
    subject: 'chem',
    topic: 'Розчини',
    text: 'pH нейтрального розчину (за 25 °C) дорівнює:',
    options: ['0', '7', '14', '1'],
    correct: 1,
    explain: 'Менше 7 — кисле середовище, більше 7 — лужне.',
  },
  {
    subject: 'chem',
    topic: 'Реакції',
    text: 'Реакція між кислотою і лугом називається реакцією:',
    options: ['нейтралізації', 'розкладу', 'горіння', 'заміщення'],
    correct: 0,
    explain: 'Кислота + луг → сіль + вода: нейтралізація.',
  },
  {
    subject: 'chem',
    topic: 'Розрахунки',
    text: 'Відносна молекулярна маса води H₂O дорівнює:',
    options: ['16', '18', '20', '10'],
    correct: 1,
    explain: '2 · 1 + 16 = 18.',
  },
  // --- Географія ------------------------------------------------------------------------------------------
  {
    subject: 'geo',
    topic: 'Україна',
    text: 'Найвища точка України:',
    options: ['Говерла', 'Роман-Кош', 'Піп Іван', 'Петрос'],
    correct: 0,
    explain: 'Говерла в Українських Карпатах — 2061 м.',
  },
  {
    subject: 'geo',
    topic: 'Україна',
    text: 'Яка річка найдовша в межах України?',
    options: ['Дністер', 'Дніпро', 'Дунай', 'Південний Буг'],
    correct: 1,
    explain: 'Дніпро — найдовша річка в межах України.',
  },
  {
    subject: 'geo',
    topic: 'Картографія',
    text: 'Масштаб 1 : 100 000 означає, що 1 см на карті відповідає:',
    options: ['100 м', '1 км', '10 км', '100 км'],
    correct: 1,
    explain: '100 000 см = 1 000 м = 1 км.',
  },
  {
    subject: 'geo',
    topic: 'Україна',
    text: 'Які моря омивають Україну?',
    options: ['Чорне та Азовське', 'Балтійське', 'Середземне', 'Каспійське'],
    correct: 0,
    explain: 'Україну омивають Чорне та Азовське моря.',
  },
  {
    subject: 'geo',
    topic: 'Світ',
    text: 'Найбільший за площею материк:',
    options: ['Африка', 'Євразія', 'Північна Америка', 'Антарктида'],
    correct: 1,
    explain: 'Євразія — понад 54 млн км².',
  },
  {
    subject: 'geo',
    topic: 'Світ',
    text: 'Столиця Польщі:',
    options: ['Краків', 'Варшава', 'Львів', 'Гданськ'],
    correct: 1,
    explain: 'Варшава.',
  },
];

/** A practice test: the subject's questions, mixed differently each time (the seed). */
export function nmtPractice(subject: NmtSubject, seed: number, size = 8): NmtQuestion[] {
  let s = seed || 1;
  const rand = () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
  const list = NMT_BANK.filter((q) => q.subject === subject);
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [list[i], list[j]] = [list[j] as NmtQuestion, list[i] as NmtQuestion];
  }
  return list.slice(0, size);
}

/** On the 100–200 scale, roughly: the real conversion depends on the year's tables. */
export function nmtApprox(correct: number, total: number): number {
  return total === 0 ? 100 : Math.round(100 + (100 * correct) / total);
}

export function daysTo(today: string, day: string): number {
  return Math.max(0, Math.round((Date.parse(day) - Date.parse(today)) / 86_400_000));
}

export interface NmtAttempt {
  subject: NmtSubject;
  correct: number;
  total: number;
  missedTopics: string[];
  takenAt: string;
}

export interface NmtPlan {
  daysLeft: number;
  weeksLeft: number;
  /** topics to repeat, the most missed first, with their subjects */
  topics: Array<{ subject: NmtSubject; topic: string; misses: number }>;
  /** this week's share */
  thisWeek: Array<{ subject: NmtSubject; topic: string }>;
}

/**
 * The plan: topics missed in the latest attempts (the last three of each subject), the most missed
 * first; a week takes three of them.
 */
export function nmtPlan(
  attempts: readonly NmtAttempt[],
  subjects: readonly NmtSubject[],
  today: string,
): NmtPlan {
  const daysLeft = daysTo(today, NMT_DAY);
  const misses = new Map<string, { subject: NmtSubject; topic: string; misses: number }>();
  for (const subject of subjects) {
    const latest = attempts
      .filter((a) => a.subject === subject)
      .sort((a, b) => b.takenAt.localeCompare(a.takenAt))
      .slice(0, 3);
    for (const a of latest) {
      for (const topic of a.missedTopics) {
        const key = `${subject}:${topic}`;
        const entry = misses.get(key) ?? { subject, topic, misses: 0 };
        entry.misses += 1;
        misses.set(key, entry);
      }
    }
  }
  const topics = [...misses.values()].sort(
    (a, b) => b.misses - a.misses || a.topic.localeCompare(b.topic, 'uk'),
  );
  return {
    daysLeft,
    weeksLeft: Math.ceil(daysLeft / 7),
    topics,
    thisWeek: topics.slice(0, 3).map(({ subject, topic }) => ({ subject, topic })),
  };
}

export function useNmt(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['nmt', studentId],
    enabled: studentId !== '',
    queryFn: async () => {
      const [choices, attempts] = await Promise.all([
        db.from('nmt_choices').select('electives').eq('student_member_id', studentId),
        db
          .from('nmt_attempts')
          .select('subject, correct, total, missed_topics, taken_at')
          .eq('student_member_id', studentId)
          .order('taken_at', { ascending: false })
          .limit(200),
      ]);
      return {
        electives: (must(choices)[0]?.electives ?? []) as NmtSubject[],
        attempts: must(attempts).map((a): NmtAttempt => ({
          subject: a.subject as NmtSubject,
          correct: a.correct,
          total: a.total,
          missedTopics: a.missed_topics,
          takenAt: a.taken_at,
        })),
      };
    },
  });
}

export function useNmtActions(schoolId: string, studentId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['nmt', studentId] });
  return {
    choose: useMutation({
      mutationFn: async (electives: NmtSubject[]) =>
        must(
          await db.from('nmt_choices').upsert({
            school_id: schoolId,
            student_member_id: studentId,
            electives,
            updated_at: new Date().toISOString(),
          }),
        ),
      onSettled: invalidate,
    }),
    save: useMutation({
      mutationFn: async (a: {
        subject: NmtSubject;
        correct: number;
        total: number;
        missed: string[];
      }) =>
        must(
          await db.from('nmt_attempts').insert({
            school_id: schoolId,
            student_member_id: studentId,
            subject: a.subject,
            correct: a.correct,
            total: a.total,
            missed_topics: [...new Set(a.missed)],
          }),
        ),
      onSettled: invalidate,
    }),
  };
}

/** The 10th and 11th grades prepare; the older the closer. */
export function preparesForNmt(grade: number | undefined): boolean {
  return grade !== undefined && grade >= 10;
}
